import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { rqClient } from "@/shared/api/instance";
import type { SystemWSMessage } from "@/shared/websocket/types";
import { websocketClient } from "@/shared/websocket/websocket-client";

export function useSystemInvalidation() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = websocketClient.subscribeToSystem(
      (message: SystemWSMessage) => {
        handleSystemMessage(message, queryClient);
      },
    );

    return unsubscribe;
  }, [queryClient]);
}

async function handleSystemMessage(
  message: SystemWSMessage,
  queryClient: ReturnType<typeof useQueryClient>,
) {
  if (message.event === "purchase_made") {
    const { product_ids, store_ids } = message.payload;

    // Invalidate products by ids
    for (const productId of product_ids) {
      await queryClient.invalidateQueries(
        rqClient.queryOptions("get", "/api/v1/products/{id}", {
          params: { path: { id: String(productId) } },
        }),
      );
    }

    // Invalidate products list
    await queryClient.invalidateQueries(
      rqClient.queryOptions("get", "/api/v1/products", {}),
    );

    // Invalidate seller products stats
    await queryClient.invalidateQueries(
      rqClient.queryOptions("get", "/api/v1/seller/products/stats", {}),
    );

    // Invalidate stores by ids
    for (const storeId of store_ids) {
      await queryClient.invalidateQueries(
        rqClient.queryOptions("get", "/api/v1/stores/{id}", {
          params: { path: { id: String(storeId) } },
        }),
      );
    }

    // Invalidate store balance for sellers
    await queryClient.invalidateQueries(
      rqClient.queryOptions("get", "/api/v1/stores/balance", {}),
    );

    // Invalidate cart (always - items may have been removed)
    await queryClient.invalidateQueries(
      rqClient.queryOptions("get", "/api/v1/cart", {}),
    );

    // Invalidate purchases for buyer
    await queryClient.invalidateQueries(
      rqClient.queryOptions("get", "/api/v1/purchases", {}),
    );
    await queryClient.invalidateQueries(
      rqClient.queryOptions("get", "/api/v1/purchases/stats", {}),
    );

    // Invalidate sales for sellers
    await queryClient.invalidateQueries(
      rqClient.queryOptions("get", "/api/v1/sales", {}),
    );
    await queryClient.invalidateQueries(
      rqClient.queryOptions("get", "/api/v1/sales/stats", {}),
    );

    // Invalidate disputes if created
    await queryClient.invalidateQueries(
      rqClient.queryOptions("get", "/api/v1/disputes", {}),
    );
  }
}
